import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";
import { useI18n } from "../../../shared/i18n/i18n";
import { slugifyKey } from "../../../shared/lib/slug";

export type BoardsView = Awaited<ReturnType<typeof trpc.boards.list.query>>;
export type BoardView = BoardsView["boards"][number];
export type BoardItemView = BoardView["items"][number];
export type BoardPurpose = BoardView["purpose"];
export type BoardKind = BoardView["kind"];
export type BoardStatusCategory = BoardView["statuses"][number]["category"];
export type BoardStatusDefinition = BoardView["statuses"][number];
export type BoardViewType = BoardView["views"][number]["type"];
export type BoardViewConfig = BoardView["views"][number]["config"];
export type BoardViewField = BoardView["views"][number]["fields"][number];
export type BoardTransitionAssessment = Awaited<ReturnType<typeof trpc.boards.checkItemTransition.query>>;

export class BoardTransitionBlockedError extends Error {
  constructor(readonly assessment: BoardTransitionAssessment) {
    super("Status requirements are not met");
    this.name = "BoardTransitionBlockedError";
  }
}

declare global {
  interface Window {
    __impactoBoardOrderLog?: BoardOrderLogEvent[];
    __impactoBoardOrderLogClear?: () => void;
  }
}

type BoardOrderLogEvent = {
  boardViewId: string;
  groupKey: string;
  order?: string[];
  phase: "optimistic" | "request" | "response-applied" | "response-skipped" | "rollback" | "rollback-skipped";
  reason?: string;
  timestamp: number;
  version: number;
  workObjectId?: string;
};

export function useBoards() {
  // Шаблон заводит настоящие поля и статусы, и их названия остаются у команды
  // навсегда — поэтому язык берётся из интерфейса того, кто создаёт доску.
  const { locale } = useI18n();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const boardsQuery = useQuery(trpc.boards.list.queryOptions());
  const boardsQueryKey = useMemo(() => trpc.boards.list.queryKey(), [trpc]);
  const data = boardsQuery.data ?? null;
  const setData = useCallback((updater: BoardsView | null | ((current: BoardsView | null) => BoardsView | null)) => {
    queryClient.setQueryData<BoardsView | null>(boardsQueryKey, (current) =>
      typeof updater === "function" ? updater(current ?? null) : updater,
    );
  }, [boardsQueryKey, queryClient]);
  const createBoardMutation = useMutation(trpc.boards.create.mutationOptions());
  const moveBoardMutation = useMutation(trpc.boards.move.mutationOptions());
  const updateBoardMutation = useMutation(trpc.boards.update.mutationOptions());
  const addWorkObjectMutation = useMutation(trpc.boards.addWorkObject.mutationOptions());
  const updateItemMutation = useMutation(trpc.boards.updateItem.mutationOptions());
  const updateStatusEntryRuleMutation = useMutation(trpc.boards.updateStatusEntryRule.mutationOptions());
  const clearStatusEntryRuleMutation = useMutation(trpc.boards.clearStatusEntryRule.mutationOptions());
  const createStatusMutation = useMutation(trpc.boards.createStatus.mutationOptions());
  const updateStatusMutation = useMutation(trpc.boards.updateStatus.mutationOptions());
  const moveStatusMutation = useMutation(trpc.boards.moveStatus.mutationOptions());
  const archiveStatusMutation = useMutation(trpc.boards.archiveStatus.mutationOptions());
  const updateWorkObjectMutation = useMutation(trpc.workObjects.update.mutationOptions());
  const archiveItemMutation = useMutation(trpc.boards.archiveItem.mutationOptions());
  const archiveBoardMutation = useMutation(trpc.boards.archive.mutationOptions());
  const createViewMutation = useMutation(trpc.boards.createView.mutationOptions());
  const duplicateViewMutation = useMutation(trpc.boards.duplicateView.mutationOptions());
  const archiveViewMutation = useMutation(trpc.boards.archiveView.mutationOptions());
  const setDefaultStatusMutation = useMutation(trpc.boards.setDefaultStatus.mutationOptions());
  const setDefaultViewMutation = useMutation(trpc.boards.setDefaultView.mutationOptions());
  const replaceViewOrderMutation = useMutation(trpc.boards.replaceViewOrder.mutationOptions());
  const moveViewOrderMutation = useMutation(trpc.boards.moveViewOrder.mutationOptions());
  const moveItemToViewGroupMutation = useMutation(trpc.boards.moveItemToViewGroup.mutationOptions());
  const updateViewMutation = useMutation(trpc.boards.updateView.mutationOptions());
  const renameViewMutation = useMutation(trpc.boards.renameView.mutationOptions());
  const changeViewTypeMutation = useMutation(trpc.boards.changeViewType.mutationOptions());
  const updateViewFieldsMutation = useMutation(trpc.boards.updateViewFields.mutationOptions());
  const updateViewGroupMutation = useMutation(trpc.boards.updateViewGroup.mutationOptions());
  const updateViewSortMutation = useMutation(trpc.boards.updateViewSort.mutationOptions());
  const updateViewLayoutMutation = useMutation(trpc.boards.updateViewLayout.mutationOptions());
  const updateViewVisibilityMutation = useMutation(trpc.boards.updateViewVisibility.mutationOptions());
  const [error, setError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [loadingViewPageKey, setLoadingViewPageKey] = useState<string | null>(null);
  const boardViewOrderVersionRef = useRef(new Map<string, number>());

  const reload = useCallback(async () => {
    setError(null);
    try {
      await boardsQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load boards.");
    }
  }, [boardsQuery.refetch]);

  const applyBoardPatch = useCallback((input: { boardId: string; name: string }) => {
    setData((current) =>
      current
        ? {
            boards: current.boards.map((board) => (board.id === input.boardId ? { ...board, name: input.name } : board)),
          }
        : current,
    );
  }, []);

  const applyBoardItemGroupPatch = useCallback((input: {
    boardId: string;
    boardItemId: string;
    fieldValues: BoardItemView["fieldValues"];
    status: BoardItemView["status"];
  }) => {
    setData((current) =>
      current
        ? {
            boards: current.boards.map((board) =>
              board.id === input.boardId
                ? {
                    ...board,
                    items: board.items.map((item) =>
                      item.id === input.boardItemId
                        ? {
                            ...item,
                            fieldValues: input.fieldValues,
                            status: input.status,
                          }
                        : item,
                    ),
                  }
                : board,
            ),
          }
        : current,
    );
  }, []);

  const applyBoardViewGroupMembershipPatch = useCallback((input: {
    boardId: string;
    boardViewId: string;
    groupKey: string;
    targetGroupValue: string;
    workObjectId: string;
  }) => {
    setData((current) => current ? {
      boards: current.boards.map((board) => board.id !== input.boardId ? board : {
        ...board,
        views: board.views.map((view) => {
          if (view.id !== input.boardViewId || view.query.groups.length === 0) return view;
          const sourceGroup = view.query.groups.find((group) => group.workObjectIds.includes(input.workObjectId));
          if (sourceGroup?.value === input.targetGroupValue) return view;
          const hasTargetGroup = view.query.groups.some((group) => group.value === input.targetGroupValue);
          return {
            ...view,
            query: {
              ...view.query,
              groups: [
                ...view.query.groups.map((group) => {
                  if (group.value === sourceGroup?.value) {
                    return {
                      ...group,
                      totalCount: Math.max(0, group.totalCount - 1),
                      workObjectIds: group.workObjectIds.filter((workObjectId) => workObjectId !== input.workObjectId),
                    };
                  }
                  if (group.value === input.targetGroupValue) {
                    return {
                      ...group,
                      totalCount: group.totalCount + 1,
                      workObjectIds: [...new Set([...group.workObjectIds, input.workObjectId])],
                    };
                  }
                  return group;
                }),
                ...(!hasTargetGroup ? [{
                  key: input.groupKey,
                  nextCursor: null,
                  totalCount: 1,
                  value: input.targetGroupValue,
                  workObjectIds: [input.workObjectId],
                }] : []),
              ],
            },
          };
        }),
      }),
    } : current);
  }, []);

  const applyBoardGroupMoveResponse = useCallback((nextData: BoardsView, input: {
    boardId: string;
    boardItemId: string;
    boardViewId: string;
  }) => {
    const nextBoard = nextData.boards.find((board) => board.id === input.boardId);
    const nextItem = nextBoard?.items.find((item) => item.id === input.boardItemId);
    const nextView = nextBoard?.views.find((view) => view.id === input.boardViewId);
    if (!nextItem || !nextView) return;
    setData((current) => current ? {
      boards: current.boards.map((board) => board.id !== input.boardId ? board : {
        ...board,
        items: board.items.map((item) => item.id === input.boardItemId ? nextItem : item),
        views: board.views.map((view) => view.id === input.boardViewId
          ? { ...view, orders: nextView.orders, query: nextView.query }
          : view),
      }),
    } : current);
  }, []);

  const applyWorkObjectFieldValuesPatch = useCallback((input: {
    fieldValues: BoardItemView["fieldValues"];
    workObjectId: string;
  }) => {
    setData((current) =>
      current
        ? {
            boards: current.boards.map((board) => ({
              ...board,
              items: board.items.map((item) =>
                item.workObject.id === input.workObjectId
                  ? {
                      ...item,
                      fieldValues: input.fieldValues,
                    }
                  : item,
              ),
            })),
          }
        : current,
    );
  }, []);

  // Merge the edited values straight into the board so the cell reflects them immediately, before
  // the mutation round-trips. Without this the editor briefly re-syncs to the still-stale value
  // after committing (new → old → new flicker). Returns a rollback for the failure path.
  const applyWorkObjectFieldValuesOptimistic = useCallback(
    (input: { values: Array<{ fieldDefinitionId: string; value: string | string[] }>; workObjectId: string }) => {
      const previous = data?.boards.flatMap((board) => board.items).find((item) => item.workObject.id === input.workObjectId)?.fieldValues ?? null;
      setData((current) =>
        current
          ? {
              boards: current.boards.map((board) => ({
                ...board,
                items: board.items.map((item) => {
                  if (item.workObject.id !== input.workObjectId) return item;
                  const fieldValues = item.fieldValues.map((fieldValue) => {
                    const patch = input.values.find((entry) => entry.fieldDefinitionId === fieldValue.fieldDefinitionId);
                    return patch ? { ...fieldValue, value: patch.value } : fieldValue;
                  });
                  for (const entry of input.values) {
                    if (!fieldValues.some((fieldValue) => fieldValue.fieldDefinitionId === entry.fieldDefinitionId)) {
                      fieldValues.push({ fieldDefinitionId: entry.fieldDefinitionId, value: entry.value });
                    }
                  }
                  return { ...item, fieldValues };
                }),
              })),
            }
          : current,
      );
      return () => {
        if (previous) applyWorkObjectFieldValuesPatch({ fieldValues: previous, workObjectId: input.workObjectId });
      };
    },
    [applyWorkObjectFieldValuesPatch, data],
  );

  async function createBoard(input: {
    description: string;
    folderId?: string | null;
    kind?: BoardKind;
    name: string;
    purpose: BoardPurpose;
    spaceId: string;
    // Optional starting configuration; without it the board is created empty.
    templateKey?: string;
  }) {
    setIsMutating(true);
    setError(null);
    const optimisticId = `optimistic-board-${crypto.randomUUID()}`;
    const optimisticBoard: BoardView = {
      availableFieldIds: [],
      availableFields: [],
      createdAt: new Date(),
      defaultStatusId: null,
      description: input.description,
      folderId: input.folderId ?? null,
      id: optimisticId,
      items: [],
      key: slugifyKey(input.name, "board"),
      kind: input.kind ?? "standard",
      name: input.name.trim(),
      purpose: input.purpose,
      rank: "zz",
      spaceId: input.spaceId,
      statusFieldDefinitionId: null,
      statuses: [],
      views: [],
      workspaceId: data?.boards[0]?.workspaceId ?? "",
    };
    setData((current) => (current ? { boards: [...current.boards, optimisticBoard] } : { boards: [optimisticBoard] }));
    try {
      const nextData = await createBoardMutation.mutateAsync({ ...input, locale });
      setData(nextData);
      return nextData;
    } catch (err) {
      setData((current) => (current ? { boards: current.boards.filter((board) => board.id !== optimisticId) } : current));
      setError(err instanceof Error ? err.message : "Could not create board.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function moveBoard(input: { boardId: string; folderId?: string | null }) {
    setIsMutating(true);
    setError(null);
    const previousData = data;
    setData((current) =>
      current
        ? {
            boards: current.boards.map((board) =>
              board.id === input.boardId
                ? {
                    ...board,
                    folderId: input.folderId ?? null,
                  }
                : board,
            ),
          }
        : current,
    );
    try {
      setData(await moveBoardMutation.mutateAsync(input));
    } catch (err) {
      setData(previousData);
      setError(err instanceof Error ? err.message : "Could not move board.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  function applyTreeOrder(input: { items: Array<{ id: string; kind: "board" | "folder"; rank?: string }>; parentFolderId?: string | null }) {
    const ranksByBoardId = new Map(
      input.items
        .map((item, index) => ({ ...item, rank: item.rank ?? optimisticTreeRank(index) }))
        .filter((item) => item.kind === "board")
        .map((item) => [item.id, item.rank]),
    );
    if (ranksByBoardId.size === 0) {
      return;
    }
    setData((current) =>
      current
        ? {
            boards: current.boards.map((board) =>
              ranksByBoardId.has(board.id)
                ? {
                    ...board,
                    folderId: input.parentFolderId ?? null,
                    rank: requiredMapValue(ranksByBoardId, board.id),
                  }
                : board,
            ),
          }
        : current,
    );
  }

  const applyBoardViewOrderPatch = useCallback(
    (input: {
      boardId: string;
      boardViewId: string;
      groupKey: string;
      orders: Array<{ rank: string; workObjectId: string }>;
    }) => {
      const nextOrders = input.orders.map((order) => ({
        groupKey: input.groupKey,
        rank: order.rank,
        workObjectId: order.workObjectId,
      }));
      setData((current) =>
        current
          ? {
              boards: current.boards.map((board) =>
                board.id === input.boardId
                  ? {
                      ...board,
                      views: board.views.map((view) =>
                        view.id === input.boardViewId
                          ? {
                              ...view,
                              orders: [
                                ...view.orders.filter((order) => order.groupKey !== input.groupKey),
                                ...nextOrders,
                              ],
                            }
                          : view,
                      ),
                    }
                  : board,
              ),
            }
          : current,
      );
    },
    [],
  );

  const applyBoardViewOrderPatchFromData = useCallback(
    (nextData: BoardsView, input: { boardId: string; boardViewId: string; groupKey: string }) => {
      const board = nextData.boards.find((item) => item.id === input.boardId);
      const view = board?.views.find((item) => item.id === input.boardViewId);
      const orders = view?.orders.filter((order) => order.groupKey === input.groupKey) ?? [];
      applyBoardViewOrderPatch({
        boardId: input.boardId,
        boardViewId: input.boardViewId,
        groupKey: input.groupKey,
        orders,
      });
    },
    [applyBoardViewOrderPatch],
  );

  const nextBoardViewOrderVersion = useCallback((input: { boardViewId: string; groupKey: string }) => {
    const key = boardViewOrderKey(input);
    const nextVersion = (boardViewOrderVersionRef.current.get(key) ?? 0) + 1;
    boardViewOrderVersionRef.current.set(key, nextVersion);
    return nextVersion;
  }, []);

  const isCurrentBoardViewOrderVersion = useCallback((input: { boardViewId: string; groupKey: string }, version: number) => {
    return boardViewOrderVersionRef.current.get(boardViewOrderKey(input)) === version;
  }, []);

  async function updateBoard(input: { boardId: string; name: string }) {
    setIsMutating(true);
    setError(null);
    const previousData = data;
    setData((current) =>
      current
        ? {
            boards: current.boards.map((board) => (board.id === input.boardId ? { ...board, name: input.name } : board)),
          }
        : current,
    );
    try {
      setData(await updateBoardMutation.mutateAsync(input));
    } catch (err) {
      setData(previousData);
      setError(err instanceof Error ? err.message : "Could not update board.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function addWorkObject(input: {
    boardId: string;
    boardStatusId?: string;
    workObjectId: string;
  }) {
    setIsMutating(true);
    setError(null);
    try {
      const nextData = await addWorkObjectMutation.mutateAsync(input);
      setData(nextData);
      return nextData;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add item to board.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function updateItem(input: { boardItemId: string; boardStatusId: string }) {
    setIsMutating(true);
    setError(null);
    try {
      const assessment = await queryClient.fetchQuery(trpc.boards.checkItemTransition.queryOptions({ boardItemId: input.boardItemId, targetStatusId: input.boardStatusId }));
      if (!assessment.allowed) {
        throw new BoardTransitionBlockedError(assessment);
      }
      setData(await updateItemMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update board item.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function updateStatusEntryRule(input: { boardId: string; expression: string; targetStatusId: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await updateStatusEntryRuleMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update status requirements.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function clearStatusEntryRule(input: { boardId: string; targetStatusId: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await clearStatusEntryRuleMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not clear status requirements.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function createStatus(input: { boardId: string; category: BoardStatusCategory; color: string; name: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await createStatusMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create board status.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function updateStatus(input: { boardId: string; boardStatusId: string; color: string; name: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await updateStatusMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update board status.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  // The status new work lands in, independent of column order.
  async function setDefaultStatus(input: { boardId: string; boardStatusId: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await setDefaultStatusMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set the default status.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function moveStatus(input: { boardId: string; boardStatusId: string; nextStatusId?: string | null; previousStatusId?: string | null }) {
    setIsMutating(true);
    setError(null);
    const previousData = data;
    setData((current) => applyStatusMove(current, input));
    try {
      setData(await moveStatusMutation.mutateAsync(input));
    } catch (err) {
      setData(previousData);
      setError(err instanceof Error ? err.message : "Could not reorder board statuses.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function archiveStatus(input: { boardId: string; boardStatusId: string; replacementStatusId?: string | null }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await archiveStatusMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive board status.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function updateWorkObjectType(input: { type: NonNullable<BoardItemView["workObject"]["type"]>; workObjectId: string }) {
    setIsMutating(true);
    setError(null);
    const previousData = data;
    const currentItem = data?.boards.flatMap((board) => board.items).find((item) => item.workObject.id === input.workObjectId);
    if (!currentItem) {
      setIsMutating(false);
      return;
    }
    setData((current) => patchWorkObjectType(current, input));
    try {
      const updated = await updateWorkObjectMutation.mutateAsync({
        id: input.workObjectId,
        title: currentItem.workObject.title,
        typeId: input.type.id,
      });
      setData((current) =>
        patchWorkObjectType(current, {
          type: updated.type,
          workObjectId: updated.id,
        }),
      );
    } catch (err) {
      setData(previousData);
      setError(err instanceof Error ? err.message : "Could not update item type.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function archiveItem(input: { boardItemId: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await archiveItemMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove item from board.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function archiveBoard(input: { boardId: string }) {
    setIsMutating(true);
    setError(null);
    const previousData = data;
    setData((current) => (current ? { boards: current.boards.filter((board) => board.id !== input.boardId) } : current));
    try {
      const nextData = await archiveBoardMutation.mutateAsync(input);
      setData(nextData);
      return nextData;
    } catch (err) {
      setData(previousData);
      setError(err instanceof Error ? err.message : "Could not archive board.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function createView(input: { boardId: string; isPersonal?: boolean; name: string; type: BoardViewType }): Promise<string | null> {
    setIsMutating(true);
    setError(null);
    try {
      const previousViewIds = new Set(data?.boards.find((board) => board.id === input.boardId)?.views.map((view) => view.id) ?? []);
      const nextData = await createViewMutation.mutateAsync(input);
      setData(nextData);
      return nextData.boards.find((board) => board.id === input.boardId)?.views.find((view) => !previousViewIds.has(view.id))?.id ?? null;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create board view.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  // Копирование целиком на сервере: копия личного вида обязана остаться личной,
  // а раньше клиент собирал её по кусочкам и флаг терялся.
  async function duplicateView(input: { boardId: string; boardViewId: string; name?: string }): Promise<string | null> {
    setIsMutating(true);
    setError(null);
    try {
      const previousViewIds = new Set(data?.boards.find((board) => board.id === input.boardId)?.views.map((view) => view.id) ?? []);
      const nextData = await duplicateViewMutation.mutateAsync(input);
      setData(nextData);
      return nextData.boards.find((board) => board.id === input.boardId)?.views.find((view) => !previousViewIds.has(view.id))?.id ?? null;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not duplicate board view.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function archiveView(input: { boardId: string; boardViewId: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await archiveViewMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive board view.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function setDefaultView(input: { boardId: string; boardViewId: string }) {
    setIsMutating(true);
    setError(null);
    try {
      setData(await setDefaultViewMutation.mutateAsync(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update default board view.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function replaceViewOrder(input: {
    boardId: string;
    boardViewId: string;
    groupKey: string;
    workObjectIds: string[];
  }) {
    setIsReordering(true);
    setError(null);
    const previousData = data;
    const orderVersion = nextBoardViewOrderVersion(input);
    recordBoardOrderLog({
      boardViewId: input.boardViewId,
      groupKey: input.groupKey,
      order: input.workObjectIds,
      phase: "optimistic",
      version: orderVersion,
    });
    applyBoardViewOrderPatch({
      boardId: input.boardId,
      boardViewId: input.boardViewId,
      groupKey: input.groupKey,
      orders: input.workObjectIds.map((workObjectId, index) => ({
        rank: optimisticOrderRank(index),
        workObjectId,
      })),
    });
    try {
      recordBoardOrderLog({
        boardViewId: input.boardViewId,
        groupKey: input.groupKey,
        order: input.workObjectIds,
        phase: "request",
        version: orderVersion,
      });
      const nextData = await replaceViewOrderMutation.mutateAsync(input);
      if (isCurrentBoardViewOrderVersion(input, orderVersion)) {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "response-applied",
          version: orderVersion,
        });
        applyBoardViewOrderPatchFromData(nextData, input);
      } else {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "response-skipped",
          reason: "stale reorder response",
          version: orderVersion,
        });
      }
    } catch (err) {
      if (isCurrentBoardViewOrderVersion(input, orderVersion)) {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "rollback",
          reason: err instanceof Error ? err.message : "Could not update board view order.",
          version: orderVersion,
        });
        setData(previousData);
      } else {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "rollback-skipped",
          reason: "stale reorder error",
          version: orderVersion,
        });
      }
      setError(err instanceof Error ? err.message : "Could not update board view order.");
      throw err;
    } finally {
      setIsReordering(false);
    }
  }

  async function moveViewOrder(input: {
    boardId: string;
    boardViewId: string;
    groupKey: string;
    nextWorkObjectId?: string | null;
    orderedWorkObjectIds: string[];
    previousWorkObjectId?: string | null;
    workObjectId: string;
  }) {
    setIsReordering(true);
    setError(null);
    const previousData = data;
    const orderVersion = nextBoardViewOrderVersion(input);
    recordBoardOrderLog({
      boardViewId: input.boardViewId,
      groupKey: input.groupKey,
      order: input.orderedWorkObjectIds,
      phase: "optimistic",
      version: orderVersion,
      workObjectId: input.workObjectId,
    });
    applyBoardViewOrderPatch({
      boardId: input.boardId,
      boardViewId: input.boardViewId,
      groupKey: input.groupKey,
      orders: input.orderedWorkObjectIds.map((workObjectId, index) => ({
        rank: optimisticOrderRank(index),
        workObjectId,
      })),
    });
    try {
      recordBoardOrderLog({
        boardViewId: input.boardViewId,
        groupKey: input.groupKey,
        order: input.orderedWorkObjectIds,
        phase: "request",
        version: orderVersion,
        workObjectId: input.workObjectId,
      });
      const nextData = await moveViewOrderMutation.mutateAsync(input);
      if (isCurrentBoardViewOrderVersion(input, orderVersion)) {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "response-applied",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
        applyBoardViewOrderPatchFromData(nextData, input);
      } else {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "response-skipped",
          reason: "stale reorder response",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
      }
    } catch (err) {
      if (isCurrentBoardViewOrderVersion(input, orderVersion)) {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "rollback",
          reason: err instanceof Error ? err.message : "Could not update board view order.",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
        setData(previousData);
      } else {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "rollback-skipped",
          reason: "stale reorder error",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
      }
      setError(err instanceof Error ? err.message : "Could not update board view order.");
      throw err;
    } finally {
      setIsReordering(false);
    }
  }

  function applyOptimisticBoardItemGroupPatch(input: {
    boardId: string;
    boardItemId: string;
    boardViewId: string;
    targetGroupValue: string;
  }) {
    setData((current) =>
      current
        ? {
            boards: current.boards.map((board) => {
              if (board.id !== input.boardId) {
                return board;
              }
              const view = board.views.find((item) => item.id === input.boardViewId);
              const groupBy = view?.config.groupBy ?? null;
              if (board.statusFieldDefinitionId && groupBy === `field:${board.statusFieldDefinitionId}`) {
                const status = board.statuses.find((item) => item.id === input.targetGroupValue);
                if (!status) {
                  return board;
                }
                return {
                  ...board,
                  items: board.items.map((item) => (item.id === input.boardItemId ? { ...item, status } : item)),
                };
              }
              if (groupBy?.startsWith("field:")) {
                const fieldId = groupBy.slice("field:".length);
                return {
                  ...board,
                  items: board.items.map((item) =>
                    item.id === input.boardItemId
                      ? {
                          ...item,
                          fieldValues: input.targetGroupValue
                            ? [
                                ...item.fieldValues.filter((value) => value.fieldDefinitionId !== fieldId),
                                { fieldDefinitionId: fieldId, value: input.targetGroupValue },
                              ]
                            : item.fieldValues.filter((value) => value.fieldDefinitionId !== fieldId),
                        }
                      : item,
                  ),
                };
              }
              return board;
            }),
          }
        : current,
    );
  }

  async function moveItemToViewGroup(input: {
    boardId: string;
    boardItemId: string;
    boardViewId: string;
    groupKey: string;
    nextWorkObjectId?: string | null;
    orderedWorkObjectIds: string[];
    previousWorkObjectId?: string | null;
    targetGroupValue: string;
    workObjectId: string;
  }) {
    setIsReordering(true);
    setError(null);
    const previousData = data;
    const orderVersion = nextBoardViewOrderVersion(input);
    recordBoardOrderLog({
      boardViewId: input.boardViewId,
      groupKey: input.groupKey,
      order: input.orderedWorkObjectIds,
      phase: "optimistic",
      version: orderVersion,
      workObjectId: input.workObjectId,
    });
    applyOptimisticBoardItemGroupPatch(input);
    applyBoardViewGroupMembershipPatch(input);
    applyBoardViewOrderPatch({
      boardId: input.boardId,
      boardViewId: input.boardViewId,
      groupKey: input.groupKey,
      orders: input.orderedWorkObjectIds.map((workObjectId, index) => ({
        rank: optimisticOrderRank(index),
        workObjectId,
      })),
    });
    try {
      recordBoardOrderLog({
        boardViewId: input.boardViewId,
        groupKey: input.groupKey,
        order: input.orderedWorkObjectIds,
        phase: "request",
        version: orderVersion,
        workObjectId: input.workObjectId,
      });
      const nextData = await moveItemToViewGroupMutation.mutateAsync(input);
      if (isCurrentBoardViewOrderVersion(input, orderVersion)) {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "response-applied",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
        applyBoardGroupMoveResponse(nextData, input);
      } else {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "response-skipped",
          reason: "stale reorder response",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
      }
    } catch (err) {
      if (isCurrentBoardViewOrderVersion(input, orderVersion)) {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "rollback",
          reason: err instanceof Error ? err.message : "Could not move work item between groups.",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
        setData(previousData);
      } else {
        recordBoardOrderLog({
          boardViewId: input.boardViewId,
          groupKey: input.groupKey,
          phase: "rollback-skipped",
          reason: "stale reorder error",
          version: orderVersion,
          workObjectId: input.workObjectId,
        });
      }
      setError(err instanceof Error ? err.message : "Could not move work item between groups.");
      throw err;
    } finally {
      setIsReordering(false);
    }
  }

  async function updateView(input: {
    boardId: string;
    boardViewId: string;
    config: BoardViewConfig;
    fields: Array<{ fieldDefinitionId: string; locked?: boolean; rank?: string; summaryOperator?: BoardViewField["summaryOperator"]; visible: boolean }>;
    name: string;
    type: BoardViewType;
  }) {
    setError(null);
    const previousData = data;
    setData((current) => patchBoardView(current, input));
    try {
      const nextData = await updateViewMutation.mutateAsync(input);
      const updatedView = nextData.boards
        .find((board) => board.id === input.boardId)
        ?.views.find((view) => view.id === input.boardViewId);
      if (updatedView) {
        setData((current) => patchBoardView(current, { ...updatedView, boardId: input.boardId, boardViewId: input.boardViewId }));
      }
    } catch (err) {
      setData(previousData);
      setError(err instanceof Error ? err.message : "Could not update board view.");
      throw err;
    }
  }

  async function mutateViewPatch(
    input: { boardId: string; boardViewId: string },
    optimistic: Partial<Pick<BoardView["views"][number], "config" | "fields" | "name" | "type">>,
    mutation: () => Promise<BoardsView>,
  ) {
    setError(null);
    const previousData = data;
    setData((current) => patchBoardViewPartial(current, input, optimistic));
    try {
      const nextData = await mutation();
      const updatedView = nextData.boards
        .find((board) => board.id === input.boardId)
        ?.views.find((view) => view.id === input.boardViewId);
      if (updatedView) {
        setData((current) => patchBoardViewPartial(current, input, updatedView));
      }
    } catch (err) {
      setData(previousData);
      setError(err instanceof Error ? err.message : "Could not update board view.");
      throw err;
    }
  }

  async function renameView(input: { boardId: string; boardViewId: string; name: string }) {
      await mutateViewPatch(input, { name: input.name }, () => renameViewMutation.mutateAsync(input));
  }

  async function changeViewType(input: { boardId: string; boardViewId: string; type: BoardViewType }) {
      await mutateViewPatch(input, { type: input.type }, () => changeViewTypeMutation.mutateAsync(input));
  }

  async function updateViewFields(input: { boardId: string; boardViewId: string; fields: Array<{ fieldDefinitionId: string; summaryOperator?: BoardViewField["summaryOperator"]; visible: boolean }> }) {
    const currentView = data?.boards.find((board) => board.id === input.boardId)?.views.find((view) => view.id === input.boardViewId);
    await mutateViewPatch(
      input,
      currentView
        ? {
            fields: input.fields.map((field, index) => {
              const currentField = currentView.fields.find((item) => item.fieldDefinitionId === field.fieldDefinitionId);
              return {
                ...field,
                locked: currentField?.locked ?? false,
                rank: optimisticOrderRank(index),
                summary: currentField?.summary ?? null,
                summaries: currentField?.summaries ?? [],
                summaryOperator: field.summaryOperator ?? null,
              };
            }),
          }
        : {},
      () => updateViewFieldsMutation.mutateAsync(input),
    );
  }

  async function updateViewGroup(input: {
    boardId: string;
    boardViewId: string;
    groupBucket?: BoardViewConfig["groupBucket"];
    groupBy: BoardViewConfig["groupBy"];
  }) {
    const currentView = data?.boards.find((board) => board.id === input.boardId)?.views.find((view) => view.id === input.boardViewId);
    const groupBucket = input.groupBucket ?? null;
    await mutateViewPatch(
      input,
      currentView ? { config: { ...currentView.config, groupBucket, groupBy: input.groupBy } } : {},
      () => updateViewGroupMutation.mutateAsync({ ...input, groupBucket }),
    );
  }

  async function updateViewSort(input: { boardId: string; boardViewId: string; sorts: BoardViewConfig["sorts"] }) {
    const currentView = data?.boards.find((board) => board.id === input.boardId)?.views.find((view) => view.id === input.boardViewId);
    await mutateViewPatch(
      input,
      currentView ? { config: { ...currentView.config, sorts: input.sorts } } : {},
      () => updateViewSortMutation.mutateAsync(input),
    );
  }

  async function updateViewLayout(input: { boardId: string; boardViewId: string; layout: Partial<NonNullable<BoardViewConfig["layout"]>> }) {
    const currentView = data?.boards.find((board) => board.id === input.boardId)?.views.find((view) => view.id === input.boardViewId);
    await mutateViewPatch(
      input,
      currentView ? { config: { ...currentView.config, layout: { ...currentView.config.layout, ...input.layout } as BoardViewConfig["layout"] } } : {},
      () => updateViewLayoutMutation.mutateAsync(input),
    );
  }

  async function updateViewVisibility(input: { boardId: string; boardViewId: string; includeClosedItems: boolean }) {
    const currentView = data?.boards.find((board) => board.id === input.boardId)?.views.find((view) => view.id === input.boardViewId);
    await mutateViewPatch(
      input,
      currentView ? { config: { ...currentView.config, includeClosedItems: input.includeClosedItems } } : {},
      () => updateViewVisibilityMutation.mutateAsync(input),
    );
  }

  async function updateViewFilter(input: { boardId: string; boardViewId: string; filterExpression: string | null }) {
    const board = data?.boards.find((candidate) => candidate.id === input.boardId);
    const view = board?.views.find((candidate) => candidate.id === input.boardViewId);
    if (!view) return;
    setIsMutating(true);
    setError(null);
    try {
      setData(await updateViewMutation.mutateAsync({
        boardId: input.boardId,
        boardViewId: input.boardViewId,
        config: { ...view.config, filterExpression: input.filterExpression },
        fields: view.fields.map((field) => ({
          fieldDefinitionId: field.fieldDefinitionId,
          summaryOperator: field.summaryOperator,
          visible: field.visible,
        })),
        name: view.name,
        type: view.type,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update view filter.");
      throw err;
    } finally {
      setIsMutating(false);
    }
  }

  async function loadNextViewPage(input: { boardId: string; boardViewId: string; groupValue?: string }) {
    const board = data?.boards.find((candidate) => candidate.id === input.boardId);
    const view = board?.views.find((candidate) => candidate.id === input.boardViewId);
    const group = input.groupValue === undefined ? null : view?.query.groups.find((candidate) => candidate.value === input.groupValue);
    const cursor = group?.nextCursor ?? (input.groupValue === undefined ? view?.query.nextCursor : null);
    const pageKey = `${input.boardViewId}:${input.groupValue ?? "__all__"}`;
    if (!view || !cursor || loadingViewPageKey === pageKey) return;
    setLoadingViewPageKey(pageKey);
    setError(null);
    try {
      const page = await queryClient.fetchQuery(trpc.boards.readViewPage.queryOptions({
        boardId: input.boardId,
        boardViewId: input.boardViewId,
        cursor,
        groupValue: input.groupValue ?? null,
        limit: 100,
      }));
      setData((current) => {
        if (!current) return current;
        return {
          boards: current.boards.map((candidate) => {
            if (candidate.id !== input.boardId) return candidate;
            const itemById = new Map(candidate.items.map((item) => [item.id, item]));
            for (const item of page.items) itemById.set(item.id, item);
            return {
              ...candidate,
              items: [...itemById.values()],
              views: candidate.views.map((candidateView) =>
                candidateView.id === input.boardViewId
                  ? {
                      ...candidateView,
                      query: {
                        groups:
                          page.groupValue === null
                            ? candidateView.query.groups
                            : candidateView.query.groups.map((candidateGroup) =>
                                candidateGroup.value === page.groupValue
                                  ? {
                                      ...candidateGroup,
                                      nextCursor: page.nextCursor,
                                      workObjectIds: [
                                        ...new Set([...candidateGroup.workObjectIds, ...page.items.map((item) => item.workObject.id)]),
                                      ],
                                    }
                                  : candidateGroup,
                              ),
                        nextCursor: page.groupValue === null ? page.nextCursor : candidateView.query.nextCursor,
                        workObjectIds: [...new Set([...candidateView.query.workObjectIds, ...page.items.map((item) => item.workObject.id)])],
                      },
                    }
                  : candidateView,
              ),
            };
          }),
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load more work items.");
      throw err;
    } finally {
      setLoadingViewPageKey(null);
    }
  }

  return {
    addWorkObject,
    applyBoardItemGroupPatch,
    applyBoardPatch,
    applyBoardViewOrderPatch,
    applyWorkObjectFieldValuesPatch,
    applyWorkObjectFieldValuesOptimistic,
    applyTreeOrder,
    archiveBoard,
    archiveItem,
    archiveStatus,
    archiveView,
    clearStatusEntryRule,
    createBoard,
    createStatus,
    createView,
    duplicateView,
    data,
    error: error ?? (boardsQuery.error instanceof Error ? boardsQuery.error.message : boardsQuery.error ? String(boardsQuery.error) : null),
    isLoading: boardsQuery.isPending,
    loadingViewPageKey,
    isMutating,
    isReordering,
    moveBoard,
    moveStatus,
    moveItemToViewGroup,
    moveViewOrder,
    loadNextViewPage,
    replaceViewOrder,
    reload,
    renameView,
    setDefaultView,
    changeViewType,
    updateBoard,
    updateItem,
    setDefaultStatus,
    updateStatus,
    updateStatusEntryRule,
    updateViewFields,
    updateViewFilter,
    updateViewGroup,
    updateViewLayout,
    updateViewVisibility,
    updateViewSort,
    updateWorkObjectType,
    updateView,
  };
}

function patchBoardView(
  current: BoardsView | null,
  input: {
    boardId: string;
    boardViewId: string;
    config: BoardViewConfig;
    fields: Array<{
      fieldDefinitionId: string;
      locked?: boolean;
      rank?: string;
      summary?: BoardViewField["summary"];
      summaries?: BoardViewField["summaries"];
      summaryOperator?: BoardViewField["summaryOperator"];
      visible: boolean;
    }>;
    isDefault?: boolean;
    name: string;
    type: BoardViewType;
  },
) {
  return current
    ? {
        boards: current.boards.map((board) =>
          board.id === input.boardId
            ? {
                ...board,
                views: board.views.map((view) =>
                  view.id === input.boardViewId
                    ? {
                        ...view,
                        config: input.config,
                        fields: input.fields.map((field, index) => ({
                          fieldDefinitionId: field.fieldDefinitionId,
                          locked: field.locked ?? view.fields.find((item) => item.fieldDefinitionId === field.fieldDefinitionId)?.locked ?? false,
                          rank: field.rank ?? optimisticOrderRank(index),
                          summary: field.summary ?? view.fields.find((item) => item.fieldDefinitionId === field.fieldDefinitionId)?.summary ?? null,
                          summaries: field.summaries ?? view.fields.find((item) => item.fieldDefinitionId === field.fieldDefinitionId)?.summaries ?? [],
                          summaryOperator:
                            field.summaryOperator ?? view.fields.find((item) => item.fieldDefinitionId === field.fieldDefinitionId)?.summaryOperator ?? null,
                          visible: field.visible,
                        })),
                        isDefault: input.isDefault ?? view.isDefault,
                        name: input.name,
                        type: input.type,
                      }
                    : view,
                ),
              }
            : board,
        ),
      }
    : current;
}

function patchBoardViewPartial(
  current: BoardsView | null,
  input: { boardId: string; boardViewId: string },
  patch: Partial<BoardView["views"][number]>,
) {
  return current
    ? {
        boards: current.boards.map((board) =>
          board.id === input.boardId
            ? {
                ...board,
                views: board.views.map((view) => (view.id === input.boardViewId ? { ...view, ...patch } : view)),
              }
            : board,
        ),
      }
    : current;
}

function applyStatusMove(
  current: BoardsView | null,
  input: { boardId: string; boardStatusId: string; nextStatusId?: string | null; previousStatusId?: string | null },
) {
  if (!current) {
    return current;
  }

  return {
    boards: current.boards.map((board) => {
      if (board.id !== input.boardId) {
        return board;
      }
      const movedStatus = board.statuses.find((status) => status.id === input.boardStatusId);
      if (!movedStatus) {
        return board;
      }
      const sameCategoryStatuses = board.statuses
        .filter((status) => status.category === movedStatus.category && status.id !== movedStatus.id)
        .sort(compareBoardStatuses);
      const nextIndex = input.nextStatusId
        ? sameCategoryStatuses.findIndex((status) => status.id === input.nextStatusId)
        : sameCategoryStatuses.length;
      const insertIndex = nextIndex < 0 ? sameCategoryStatuses.length : nextIndex;
      const reorderedCategory = [
        ...sameCategoryStatuses.slice(0, insertIndex),
        movedStatus,
        ...sameCategoryStatuses.slice(insertIndex),
      ].map((status, index) => ({ ...status, sortOrder: (index + 1) * 10 }));

      return {
        ...board,
        statuses: board.statuses
          .filter((status) => status.category !== movedStatus.category)
          .concat(reorderedCategory)
          .sort(compareBoardStatuses),
      };
    }),
  };
}

function compareBoardStatuses(first: BoardStatusDefinition, second: BoardStatusDefinition) {
  return first.category.localeCompare(second.category) || first.sortOrder - second.sortOrder || first.name.localeCompare(second.name);
}

function patchWorkObjectType(
  current: BoardsView | null,
  input: { type: BoardItemView["workObject"]["type"]; workObjectId: string },
) {
  return current
    ? {
        boards: current.boards.map((board) => ({
          ...board,
          items: board.items.map((item) =>
            item.workObject.id === input.workObjectId
              ? {
                  ...item,
                  workObject: {
                    ...item.workObject,
                    type: input.type,
                  },
                }
              : item,
          ),
        })),
      }
    : current;
}

function requiredMapValue(map: Map<string, string>, key: string) {
  const value = map.get(key);
  if (!value) {
    throw new Error("Expected tree rank to exist");
  }
  return value;
}

function boardViewOrderKey(input: { boardViewId: string; groupKey: string }) {
  return `${input.boardViewId}:${input.groupKey}`;
}

const boardOrderLogMarkerId = "impacto-board-order-log";

function recordBoardOrderLog(event: Omit<BoardOrderLogEvent, "timestamp">) {
  if (!import.meta.env.DEV || typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  const nextEvent = { ...event, timestamp: Date.now() };
  window.__impactoBoardOrderLog = [...(window.__impactoBoardOrderLog ?? []), nextEvent].slice(-500);
  window.__impactoBoardOrderLogClear ??= () => {
    window.__impactoBoardOrderLog = [];
    document.getElementById(boardOrderLogMarkerId)?.remove();
  };
  const marker = document.getElementById(boardOrderLogMarkerId) ?? document.createElement("div");
  const previousLog = parseBoardOrderLogMarker(marker.getAttribute("data-log"));
  const nextLog = [...previousLog, nextEvent].slice(-200);
  marker.id = boardOrderLogMarkerId;
  marker.setAttribute("data-last", JSON.stringify(nextEvent));
  marker.setAttribute("data-log", JSON.stringify(nextLog));
  marker.setAttribute("data-phase", nextEvent.phase);
  marker.hidden = true;
  if (!marker.parentElement) {
    document.body.append(marker);
  }
  console.info("[impacto:board-order]", JSON.stringify(nextEvent));
}

function parseBoardOrderLogMarker(value: string | null): BoardOrderLogEvent[] {
  if (!value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is BoardOrderLogEvent => Boolean(item)) : [];
  } catch {
    return [];
  }
}

function optimisticTreeRank(index: number) {
  return optimisticOrderRank(index);
}

function optimisticOrderRank(index: number) {
  const digits = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  return `a${digits[index] ?? "z"}`;
}
